import { AdapterRegistry } from 'app/model/adapters/adapter-registry';
import { ActivityAsyncAdapter } from './activity.adapter';
import { Metamodel } from 'app/model/base/model';
import _ from 'lodash';
import { AdapterContext, ITridentNotebookActivity, ReferenceAdapter, TridentNotebookActivity, FabricNotebookLinkedService, ModelConstants } from 'shell-models-lib';
import { toParameterObject, toParameterClass } from '../linkedservices/parameter-config.util';
import { convertParameterValue } from 'app/shared/utils/parameter-converter.util';
import { isString, stringIsNullOrEmpty } from 'app/shared/utils/string.util';
import { ValueAdapter } from '../value.adapter';
import { getServiceInstance } from 'app/multi-contexts/modules/root/root.application';
import { ExposureControlService } from 'app/services/exposure-control/base-exposure-control.service';
import { linkedServiceReferenceWithoutParameterizationRemainer } from '../reference-remainer';
import { ITridentNotebookActivityContext } from 'app/interfaces/copilot.pipeline.action.types';
import { CopilotNameMapperService } from 'app/services/copilot-name-mapper.service';
import { isLogicAppExpression } from 'app/shared/utils/expression.util';
import { GlobalVariables } from 'app/services/globals';
import { getFabricLsForParameterizedConnections } from '../utils';

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(TridentNotebookActivity),
    asynchronous: true,
    adapts: {
        name: '*',
        description: '*',
        type: '*',
        linkedServiceName: async (context: AdapterContext) => {
            const exposureControlService = await getServiceInstance<ExposureControlService>(
                context.contextData,
                'ExposureControlService'
            );
            return exposureControlService.UseTokenServiceInNotebookActivity ? linkedServiceReferenceWithoutParameterizationRemainer : undefined;
        },
        policy: '*',
        dependsOn: '*',
        userProperties: '*',
        typeProperties: {
            notebookId: '*',
            workspaceId: '*',
            parameters: '*',
            sessionTag: '*',
            driverCores: '*',
            driverMemory: '*',
            executorCores: '*',
            executorMemory: '*',
            dynamicExecutorAllocation: '*',
            numExecutors: '*',
            minExecutors: '*',
            maxExecutors: '*',
            sparkProperties: '*',
            showEnrichedOutput: '*'
        },
        externalReferences: '*'
    }
})
export class TridentNotebookActivityAdapter extends ActivityAsyncAdapter<TridentNotebookActivity, ITridentNotebookActivity, ITridentNotebookActivityContext> {

    public override async toFabricCopilotContext(activity: TridentNotebookActivity): Promise<ITridentNotebookActivityContext> {
        if (activity) {
            const activityContext: ITridentNotebookActivityContext = await super.toFabricCopilotContext(activity);
            activityContext.NotebookId = ValueAdapter.toResource(activity, TridentNotebookActivity.NOTEBOOKID, true);
            activityContext.WorkspaceId = ValueAdapter.toResource(activity, TridentNotebookActivity.WORKSPACEID, true);
            const nameMapperService = await getServiceInstance<CopilotNameMapperService>(
                this.context.contextData,
                'CopilotNameMapperService'
            );

            if (nameMapperService) {
                const [tridentNotebookName, tridentNotebookWorkspaceName] = await Promise.all([
                    nameMapperService.getTridentArtifactNameById('pbinotebook', activityContext.NotebookId),
                    nameMapperService.getTridentArtifactNameById('workspace', activityContext.WorkspaceId)
                ]);
                if (tridentNotebookName) {
                    activityContext.NotebookName = tridentNotebookName;
                }
                if (tridentNotebookWorkspaceName) {
                    activityContext.WorkspaceName = tridentNotebookWorkspaceName;
                }
            }
            return activityContext;
        }
        return undefined;
    }

    public override async fromFabricCopilotContext(
        context: ITridentNotebookActivityContext,
        model?: TridentNotebookActivity
    ): Promise<TridentNotebookActivity> {
        if (context) {
            model = await super.fromFabricCopilotContext(context, model);
            ValueAdapter.toModel(model, TridentNotebookActivity.NOTEBOOKID, context.NotebookId);
            ValueAdapter.toModel(model, TridentNotebookActivity.WORKSPACEID, context.WorkspaceId);
        }
        return model;
    }

    public async postToModel(activity: TridentNotebookActivity): Promise<void> {
        await super.postToModel(activity);
        if (!__IS_FABRIC_DPE__) {
            return;
        }
        const nameMapperService = await getServiceInstance<CopilotNameMapperService>(
            this.context.contextData,
            'CopilotNameMapperService'
        );
        const notebookId = ValueAdapter.toResource(activity, TridentNotebookActivity.NOTEBOOKID, true);
        const workspaceId = ValueAdapter.toResource(activity, TridentNotebookActivity.WORKSPACEID, true);

        if (notebookId && !isLogicAppExpression(notebookId)) {
            nameMapperService.getTridentArtifactNameById('pbinotebook', notebookId);
        }
        if (workspaceId && !isLogicAppExpression(workspaceId)) {
            nameMapperService.getTridentArtifactNameById('workspace', workspaceId);
        }
    }

    protected async _toResource(activity: TridentNotebookActivity): Promise<ITridentNotebookActivity> {
        const modelTridentNotebookActivity = super._getCommonActivityProperties(
            activity, Metamodel.modelFor(TridentNotebookActivity).name) as ITridentNotebookActivity;

        const exposureControlService = await getServiceInstance<ExposureControlService>(
            this.context.contextData,
            'ExposureControlService'
        );

        if (exposureControlService.UseTokenServiceInNotebookActivity && activity.linkedService) {
            modelTridentNotebookActivity.linkedServiceName = this._toLinkedServiceReferenceModel(activity.linkedService,
                activity.linkedServiceParameters);
        }

        modelTridentNotebookActivity.typeProperties = {
            notebookId: activity.notebook?.objectId ?? ValueAdapter.toResource(activity, TridentNotebookActivity.NOTEBOOKID),
            workspaceId: activity.notebook?.workspaceId ?? ValueAdapter.toResource(activity, TridentNotebookActivity.WORKSPACEID),
            parameters: toParameterObject(activity.parameters, true),
            driverCores: ValueAdapter.toResource(activity, TridentNotebookActivity.SPARK_DRIVER_CORE),
            driverMemory: ValueAdapter.toResource(activity, TridentNotebookActivity.SPARK_DRIVER_MEMORY),
            executorCores: ValueAdapter.toResource(activity, TridentNotebookActivity.SPARK_EXECUTOR_CORE),
            executorMemory: ValueAdapter.toResource(activity, TridentNotebookActivity.SPARK_EXECUTOR_MEMORY),
            dynamicExecutorAllocation: ValueAdapter.toResource(activity, TridentNotebookActivity.DYNAMIC_ALLOCATION),
            numExecutors: ValueAdapter.toResource(activity, TridentNotebookActivity.SPARK_EXECUTOR_INSTANCES),
            minExecutors: ValueAdapter.toResource(activity, TridentNotebookActivity.MIN_EXECUTORS),
            maxExecutors: ValueAdapter.toResource(activity, TridentNotebookActivity.MAX_EXECUTORS),
            sparkProperties: toParameterObject(activity.sparkProperties, true)
        };

        if (GlobalVariables.enableEnrichedOutputInNotebook) {
            modelTridentNotebookActivity.typeProperties.showEnrichedOutput = ValueAdapter.toResource(
                activity,
                TridentNotebookActivity.SHOW_ENRICHED_OUTPUT
            );
        }

        if (!stringIsNullOrEmpty(activity.sessionTag?.trim()) || TridentNotebookActivity.SESSIONTAG.templateValue(activity) != undefined) {
            modelTridentNotebookActivity.typeProperties.sessionTag = ValueAdapter.toResource(activity, TridentNotebookActivity.SESSIONTAG);
        }

        if (modelTridentNotebookActivity.typeProperties.parameters) {
            for (const parameterName in modelTridentNotebookActivity.typeProperties.parameters) {
                const parameter = modelTridentNotebookActivity.typeProperties.parameters[parameterName];
                if (parameter.type === 'bool' && isString(parameter.value)) {
                    parameter.value = convertParameterValue(parameter.value, parameter.type, 'TridentNotebookActivityAdapter',
                        this.ensureLogger(), true /* don't parse as an object */);
                }
            }
        }

        if (activity.raw) {
            const parsed = JSON.parse(activity.raw);
            _.merge(modelTridentNotebookActivity, parsed);
        }

        if (!GlobalVariables.enableEnrichedOutputInNotebook) {
            delete modelTridentNotebookActivity.typeProperties.showEnrichedOutput;
        }

        return modelTridentNotebookActivity;
    }

    protected async _toModel(modelActivity: ITridentNotebookActivity): Promise<TridentNotebookActivity> {
        const activity: TridentNotebookActivity = TridentNotebookActivity.MODEL.create<TridentNotebookActivity>(modelActivity.name, modelActivity.description);
        this._updatedCommonProperties(modelActivity, activity);

        ValueAdapter.toModel(activity, TridentNotebookActivity.NOTEBOOKID, modelActivity.typeProperties.notebookId);
        ValueAdapter.toModel(activity, TridentNotebookActivity.WORKSPACEID, modelActivity.typeProperties.workspaceId);
        ValueAdapter.toModel(activity, TridentNotebookActivity.SESSIONTAG, modelActivity.typeProperties.sessionTag);
        ValueAdapter.toModel(activity, TridentNotebookActivity.SPARK_DRIVER_CORE, modelActivity.typeProperties.driverCores);
        ValueAdapter.toModel(activity, TridentNotebookActivity.SPARK_DRIVER_MEMORY, modelActivity.typeProperties.driverMemory);
        ValueAdapter.toModel(activity, TridentNotebookActivity.SPARK_EXECUTOR_CORE, modelActivity.typeProperties.executorCores);
        ValueAdapter.toModel(activity, TridentNotebookActivity.SPARK_EXECUTOR_MEMORY, modelActivity.typeProperties.executorMemory);
        ValueAdapter.toModel(activity, TridentNotebookActivity.DYNAMIC_ALLOCATION, modelActivity.typeProperties.dynamicExecutorAllocation);
        ValueAdapter.toModel(activity, TridentNotebookActivity.SPARK_EXECUTOR_INSTANCES, modelActivity.typeProperties.numExecutors);
        ValueAdapter.toModel(activity, TridentNotebookActivity.MIN_EXECUTORS, modelActivity.typeProperties.minExecutors);
        ValueAdapter.toModel(activity, TridentNotebookActivity.MAX_EXECUTORS, modelActivity.typeProperties.maxExecutors);
        if (GlobalVariables.enableEnrichedOutputInNotebook) {
            ValueAdapter.toModel(
                activity,
                TridentNotebookActivity.SHOW_ENRICHED_OUTPUT,
                modelActivity.typeProperties.showEnrichedOutput
            );
        }
        activity.sparkProperties = toParameterClass(modelActivity.typeProperties.sparkProperties, activity, true);
        activity.parameters = toParameterClass(modelActivity.typeProperties.parameters, activity, true);

        const exposureControlService = await getServiceInstance<ExposureControlService>(
            this.context.contextData,
            'ExposureControlService'
        );

        if (exposureControlService.UseTokenServiceInNotebookActivity && modelActivity.linkedServiceName != undefined) {
            activity.linkedService = await getFabricLsForParameterizedConnections(
                        modelActivity.linkedServiceName,
                        ModelConstants.fabricNotebookLinkedService, 
                        activity);
            
            //If Parameterized Connection retrival is undefined that means its not parameterized and we are continuing with regular conversion.
            if (activity.linkedService === undefined)
            {
                activity.linkedService = ReferenceAdapter.toProxyModel<FabricNotebookLinkedService>(Metamodel.modelFor(FabricNotebookLinkedService),
                    modelActivity.linkedServiceName,
                    this.context);
            }
            if (GlobalVariables.enforceConnectionForNewActivity) {
                activity.isConnectionRequired = true;
            }
        }
        activity.raw = JSON.stringify(await ValueAdapter.remainder(this.context, modelActivity, TridentNotebookActivity.MODEL));
        return activity;
    }
}
